import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AccessPermission,
  type AccessPermissionConsent,
  clearExtensionPreferenceMutation,
  type DataResponseListExtensionConsent,
  errorMessage,
  listExtensionConsentQueryKey,
  setExtensionPreferenceMutation,
} from "@/api";

const busyKey = (slug: string, permission: AccessPermission) => `${slug}:${permission}`;

export function useConsentActions() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("settings");
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const setPreference = useMutation(setExtensionPreferenceMutation());
  const clearPreference = useMutation(clearExtensionPreferenceMutation());

  const patch = (slug: string, permission: AccessPermission, next: Partial<AccessPermissionConsent>) =>
    queryClient.setQueryData<DataResponseListExtensionConsent>(listExtensionConsentQueryKey(), (data) =>
      data
        ? {
            ...data,
            data: data.data.map((ext) =>
              ext.slug === slug ? { ...ext, [permission]: { ...ext[permission], ...next } } : ext,
            ),
          }
        : data,
    );

  const track = async (slug: string, permission: AccessPermission, work: () => Promise<void>) => {
    const key = busyKey(slug, permission);
    setBusy((prev) => new Set(prev).add(key));
    try {
      await work();
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const toggle = (slug: string, permission: AccessPermission, enabled: boolean) =>
    track(slug, permission, async () => {
      try {
        const res = await setPreference.mutateAsync({ path: { slug }, body: { permission, enabled } });
        patch(slug, permission, res.data[permission]);
      } catch (error) {
        toast.error(errorMessage(error, t("extensions.saveFailed")));
      }
    });

  const reset = (slug: string, permission: AccessPermission, consentMode: string) =>
    track(slug, permission, async () => {
      try {
        await clearPreference.mutateAsync({ path: { slug }, query: { permission } });
        patch(slug, permission, { hasExplicitPreference: false, effectiveEnabled: consentMode === "opt_out" });
      } catch (error) {
        toast.error(errorMessage(error, t("extensions.resetFailed")));
      }
    });

  return { toggle, reset, isBusy: (slug: string, permission: AccessPermission) => busy.has(busyKey(slug, permission)) };
}
